<template>
  <div class="ig-employee-detail-page">
    <div class="page-container" v-loading="loading">
      <header class="page-header">
        <div class="header-main">
          <el-button
            class="back-button"
            icon="el-icon-arrow-left"
            circle
            @click="goBack"
          />
          <div>
            <div class="page-title">员工入职信息详情</div>
          </div>
        </div>
        <el-button v-if="baseInfo.isPush!=1" icon="el-icon-edit-outline" @click="goToEdit">
          编辑资料
        </el-button>
      </header>

      <section class="profile-card">
        <div class="profile-avatar">
          {{ employeeInitial }}
        </div>
        <div class="profile-content">
          <div class="profile-name">
            {{ displayValue(baseInfo.Title) }}
            <el-tag
              :type="Number(baseInfo.isPush) === 1 ? 'success' : 'info'"
              size="small"
            >
              {{ Number(baseInfo.isPush) === 1 ? "已推送" : "未推送" }}
            </el-tag>
          </div>
          <div class="profile-meta">
            <span>
              <i class="el-icon-office-building"></i>
              {{ displayValue(baseInfo.OrgName) }}
            </span>
            <span>
              <i class="el-icon-mobile-phone"></i>
              {{ displayValue(baseInfo.MobileNo) }}
            </span>
            <span>
              <i class="el-icon-time"></i>
              {{ formatCreated(baseInfo.created) }}
            </span>
          </div>
        </div>
      </section>

      <section class="detail-card">
        <div class="section-title">
          <i class="el-icon-user-solid"></i>
          <span>个人基础信息</span>
        </div>
        <div class="detail-grid">
          <div
            v-for="item in baseFields"
            :key="item.label"
            class="detail-item"
            :class="{ wide: item.wide }"
          >
            <div class="detail-label">{{ item.label }}</div>
            <div class="detail-value">{{ displayValue(item.value) }}</div>
          </div>
        </div>
      </section>

      <section class="detail-card">
        <div class="section-title">
          <i class="el-icon-phone"></i>
          <span>紧急联系人</span>
        </div>
        <div class="detail-grid">
          <div
            v-for="item in emergencyFields"
            :key="item.label"
            class="detail-item"
          >
            <div class="detail-label">{{ item.label }}</div>
            <div class="detail-value">{{ displayValue(item.value) }}</div>
          </div>
        </div>
      </section>

      <section class="detail-card">
        <div class="section-title">
          <i class="el-icon-s-custom"></i>
          <span>家庭成员信息</span>
          <span class="section-count">{{ familyInfo.length }} 条</span>
        </div>
        <div class="table-scroll">
          <el-table
            class="detail-table"
            :data="familyInfo"
            border
            stripe
            empty-text="暂无家庭成员信息"
          >
            <el-table-column type="index" label="序号" width="65" align="center" />
            <el-table-column prop="Name" label="姓名" min-width="100" />
            <el-table-column label="与本人关系" min-width="115">
              <template slot-scope="{ row }">
                {{ getOptionLabel("Relation", row.Relation) }}
              </template>
            </el-table-column>

            <el-table-column prop="TelNo" label="联系电话" min-width="130" />
            <el-table-column
              prop="WorkOrg"
              label="工作单位"
              min-width="150"
              show-overflow-tooltip
            >
              <template slot-scope="{ row }">
                {{ displayValue(row.WorkOrg) }}
              </template>
            </el-table-column>
            <el-table-column prop="WorkPost" label="职务" min-width="110">
              <template slot-scope="{ row }">
                {{ displayValue(row.WorkPost) }}
              </template>
            </el-table-column>
            <el-table-column
              prop="HJAddress"
              label="联系地址"
              min-width="200"
              show-overflow-tooltip
            >
              <template slot-scope="{ row }">
                {{ displayValue(row.HJAddress) }}
              </template>
            </el-table-column>
          </el-table>
        </div>
      </section>

      <section class="detail-card">
        <div class="section-title">
          <i class="el-icon-reading"></i>
          <span>教育经历</span>
          <span class="section-count">{{ educationInfo.length }} 条</span>
        </div>
        <div class="table-scroll">
          <el-table
            class="detail-table"
            :data="educationInfo"
            border
            stripe
            empty-text="暂无教育经历"
          >
            <el-table-column type="index" label="序号" width="65" align="center" />
            <el-table-column
              prop="School"
              label="学校"
              min-width="160"
              show-overflow-tooltip
            />
            <el-table-column label="学历" min-width="150">
              <template slot-scope="{ row }">
                {{ getOptionLabel("Education", row.Education) }}
              </template>
            </el-table-column>
            <el-table-column prop="Major" label="专业" min-width="120">
              <template slot-scope="{ row }">
                {{ displayValue(row.Major) }}
              </template>
            </el-table-column>
            <el-table-column prop="Years" label="学制" min-width="90">
              <template slot-scope="{ row }">
                {{ displayValue(row.Years) }}
              </template>
            </el-table-column>
            <el-table-column
              prop="BeginDate"
              label="入学时间"
              min-width="115"
              align="center"
            />
            <el-table-column
              prop="EndDate"
              label="毕业时间"
              min-width="115"
              align="center"
            />
            <el-table-column label="学习形式" min-width="110">
              <template slot-scope="{ row }">
                {{ getOptionLabel("StudyType", row.StudyType) }}
              </template>
            </el-table-column>
            <el-table-column prop="No" label="证书编号" min-width="130">
              <template slot-scope="{ row }">
                {{ displayValue(row.No) }}
              </template>
            </el-table-column>
          </el-table>
        </div>
      </section>

      <section class="detail-card">
        <div class="section-title">
          <i class="el-icon-suitcase"></i>
          <span>工作经历</span>
          <span class="section-count">{{ workInfo.length }} 条</span>
        </div>
        <div class="table-scroll">
          <el-table
            class="detail-table"
            :data="workInfo"
            border
            stripe
            empty-text="暂无工作经历"
          >
            <el-table-column type="index" label="序号" width="65" align="center" />
            <el-table-column
              prop="Organization"
              label="工作单位"
              min-width="160"
              show-overflow-tooltip
            />
            <el-table-column label="行业" min-width="110">
              <template slot-scope="{ row }">
                {{ getCompatibleField(row, "Industry") }}
              </template>
            </el-table-column>
            <el-table-column prop="OrgName" label="部门" min-width="120">
              <template slot-scope="{ row }">
                {{ displayValue(row.OrgName) }}
              </template>
            </el-table-column>
            <el-table-column prop="Position" label="职务" min-width="110" />
            <el-table-column
              prop="BeginDate"
              label="入职时间"
              min-width="115"
              align="center"
            />
            <el-table-column
              prop="EndDate"
              label="离职时间"
              min-width="115"
              align="center"
            />
            <el-table-column
              prop="Duty"
              label="工作内容"
              min-width="180"
              show-overflow-tooltip
            >
              <template slot-scope="{ row }">
                {{ displayValue(row.Duty) }}
              </template>
            </el-table-column>
            <el-table-column prop="referenceBy" label="证明人" min-width="100">
              <template slot-scope="{ row }">
                {{ displayValue(row.referenceBy) }}
              </template>
            </el-table-column>
            <el-table-column
              prop="referenceTel"
              label="证明人电话"
              min-width="130"
            >
              <template slot-scope="{ row }">
                {{ displayValue(row.referenceTel) }}
              </template>
            </el-table-column>
          </el-table>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import api from "@/apis";

const OPTION_CONFIGS = [
  { type: "Gender" },
  { type: "householdRegister" },
  { type: "Nation" },
  { type: "Party" },
  { type: "MaritalStatus" },
  { type: "EmContactRel" },
  { type: "Relation" },
  { type: "Education" },
  { type: "StudyType" },
  { type: "JoinType" },
];

export default {
  name: "EmployeeInformationDetail",
  data() {
    return {
      loading: false,
      applyId: "",
      baseInfo: {},
      emergencyInfo: {},
      familyInfo: [],
      educationInfo: [],
      workInfo: [],
      optionMaps: {},
    };
  },
  computed: {
    employeeInitial() {
      const name = String(this.baseInfo.Title || "");
      return name ? name.slice(0, 1) : "员";
    },
    baseFields() {
      return [
        { label: "姓名", value: this.baseInfo.Title },
        {
          label: "性别",
          value: this.getOptionLabel("Gender", this.baseInfo.Gender),
        },
        { label: "出生日期", value: this.baseInfo.BirthDate },
        {
          label: "民族",
          value: this.getOptionLabel("Nation", this.baseInfo.Nation),
        },
        { label: "籍贯", value: this.baseInfo.NativePlace },
        {
          label: "户籍性质",
          value: this.getOptionLabel(
            "householdRegister",
            this.baseInfo.householdRegister
          ),
        },
        {
          label: "婚姻状况",
          value: this.getOptionLabel(
            "MaritalStatus",
            this.baseInfo.MaritalStatus
          ),
        },
        {
          label: "政治面貌",
          value: this.getOptionLabel("Party", this.baseInfo.Party),
        },
        { label: "身份证号", value: this.baseInfo.IDNo },
        { label: "身份证有效期", value: this.baseInfo.IDENDDATE },
        { label: "身份证签发机构", value: this.baseInfo.IDAddress },
        { label: "联系电话", value: this.baseInfo.MobileNo },
        {
          label: "身份证地址",
          value: this.baseInfo.PermanentAddress,
          wide: true,
        },
        {
          label: "家庭住址",
          value: this.baseInfo.LiveAddress,
          wide: true,
        },
        { label: "电子邮箱", value: this.baseInfo.Email_P },
        { label: "部门/车间", value: this.baseInfo.OrgName },
        {
          label: "招聘渠道",
          value: this.getOptionLabel("JoinType", this.baseInfo.Channel),
        },
        {
          label: "派遣公司",
          value: this.baseInfo.PqCompany,
        },
        {
          label: "介绍人姓名",
          value: this.getCompatibleField(this.baseInfo, "Introducer"),
        },
        { label: "介绍人工号", value: this.baseInfo.InWorkCode },
        { label: "招聘学校", value: this.baseInfo.School },
        { label: "招聘网站", value: this.baseInfo.Website },
        {
          label: "其他渠道",
          value: this.getRestTypeLabel(this.baseInfo.RestType),
        },
        { label: "银行卡号", value: this.baseInfo.BankCode },
        { label: "开户行", value: this.baseInfo.Bank },
        // { label: "填写人", value: this.baseInfo.Submitor },
        { label: "提交时间", value: this.formatCreated(this.baseInfo.created) },
        { label: "手机号", value: this.baseInfo.ApplyID },
      ];
    },
    emergencyFields() {
      return [
        { label: "紧急联系人", value: this.emergencyInfo.EmContact },
        {
          label: "与本人关系",
          value: this.getOptionLabel(
            "EmContactRel",
            this.emergencyInfo.EmContactRel
          ),
        },
        { label: "联系电话", value: this.emergencyInfo.EmContactTel },
      ];
    },
  },
  created() {
    this.applyId = String(this.$route.query.id || "");
    if (!this.applyId) {
      this.$message.warning("缺少员工详情参数 id");
      this.$router.replace("/employee/list");
      return;
    }
    this.loadPage();
  },
  methods: {
    displayValue(value) {
      return value === null || value === undefined || value === ""
        ? "-"
        : value;
    },
    formatCreated(value) {
      if (!value || String(value).startsWith("0000-00-00")) return "-";
      return value;
    },
    getOptionLabel(type, value) {
      if (value === null || value === undefined || value === "") return "-";
      const options = this.optionMaps[type] || {};
      return options[String(value)] || value;
    },
    getCompatibleField(data, prefix) {
      const target = data || {};
      const key = Object.keys(target).find((item) => item.startsWith(prefix));
      return key ? this.displayValue(target[key]) : "-";
    },
    getRestTypeLabel(value) {
      if (!value || Number(value) === 0) return "-";
      const labels = {
        1: "现场",
        2: "广告",
      };
      return labels[value] || value;
    },
    normalizeOptionMap(data) {
      return Object.values(data || {}).reduce((result, item) => {
        const value = item.ID;
        const label = String(item.Title || "")
          .replace(/\r?\n/g, "")
          .trim();
        if (value && label) {
          result[String(value)] = label;
        }
        return result;
      }, {});
    },
    async loadOptions() {
      const results = await Promise.all(
        OPTION_CONFIGS.map(async ({ type }) => {
          try {
            const res = await api.getPreEmployeeOptions({ type });
            return {
              type,
              options: this.normalizeOptionMap(res.data),
            };
          } catch (error) {
            return {
              type,
              options: {},
            };
          }
        })
      );
      results.forEach(({ type, options }) => {
        this.$set(this.optionMaps, type, options);
      });
    },
    async loadDetail() {
      const res = await api.getPreEmployeeDetail({
        id: this.applyId,
      });
      const detail = res.data || {};
      this.baseInfo = detail.preEmpinfo || {};
      this.emergencyInfo = detail.PreBgeminfo || {};
      this.familyInfo = Array.isArray(detail.PreFamilyinfo)
        ? detail.PreFamilyinfo
        : [];
      this.educationInfo = Array.isArray(detail.PreEduinfo)
        ? detail.PreEduinfo
        : [];
      this.workInfo = Array.isArray(detail.PreWorkexpinfo)
        ? detail.PreWorkexpinfo
        : [];
    },
    async loadPage() {
      this.loading = true;
      try {
        await Promise.all([this.loadDetail(), this.loadOptions()]);
      } catch (error) {
        this.$message.error("员工详情加载失败，请稍后重试");
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.push("/employee/list");
    },
    goToEdit() {
      this.$router.push({
        path: "/employee/info",
        query: {
          id: this.applyId,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ig-employee-detail-page {
  width: 100%;
  min-height: 100vh;
  padding: 28px;
  font-size: 22px;
  color: #303133;
  background: #f3f6f5;
  box-sizing: border-box;
}

/* PC 端使用全部可用宽度，不限制内容最大宽度 */
.page-container {
  width: 100%;
  min-height: 260px;
  margin: 0;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 96px;
  padding: 20px 28px;
  color: #fff;
  background: linear-gradient(135deg, #178a50, #20aa68);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(20, 126, 74, 0.18);
  box-sizing: border-box;

  p {
    margin: 7px 0 0;
    font-size: 22px;
    color: rgba(255, 255, 255, 0.82);
  }
}

.header-main {
  display: flex;
  align-items: center;
}

.back-button {
  margin-right: 16px;
  color: #fff;
  border-color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.12);
}

.page-title {
  font-size: 30px;
  font-weight: 600;
}

.profile-card {
  display: flex;
  align-items: center;
  margin-top: 20px;
  padding: 24px 28px;
  background: #fff;
  border: 1px solid #e5ece8;
  border-radius: 12px;
  box-shadow: 0 5px 18px rgba(31, 67, 49, 0.06);
}

.profile-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 68px;
  margin-right: 20px;
  font-size: 30px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #1fa463, #55be86);
  border-radius: 50%;
}

.profile-content {
  min-width: 0;
}

.profile-name {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 28px;
  font-weight: 600;
  color: #26372e;
}

.profile-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 26px;
  margin-top: 12px;
  font-size: 22px;
  color: #758179;

  i {
    margin-right: 5px;
    color: #1fa463;
  }
}

.detail-card {
  margin-top: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5ece8;
  border-radius: 12px;
  box-shadow: 0 5px 18px rgba(31, 67, 49, 0.06);
}

.section-title {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 14px;
  font-size: 22px;
  font-weight: 600;
  color: #26372e;
  border-bottom: 1px solid #edf1ef;

  i {
    margin-right: 9px;
    font-size: 22px;
    color: #1fa463;
  }
}

.section-count {
  margin-left: 10px;
  padding: 3px 9px;
  font-size: 22px;
  font-weight: 400;
  color: #1b9258;
  background: #eaf7f0;
  border-radius: 12px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-top: 1px solid #edf1ef;
  border-left: 1px solid #edf1ef;
}

.detail-item {
  min-height: 78px;
  padding: 14px 16px;
  border-right: 1px solid #edf1ef;
  border-bottom: 1px solid #edf1ef;
  box-sizing: border-box;
}

.detail-item.wide {
  grid-column: span 2;
}

.detail-label {
  margin-bottom: 8px;
  font-size: 22px;
  color: #8b958f;
}

.detail-value {
  overflow-wrap: anywhere;
  font-size: 24px;
  line-height: 1.55;
  color: #303b35;
}

.table-scroll {
  width: 100%;
  overflow-x: auto;
}

.detail-table {
  min-width: 1000px;
}

::v-deep .detail-table th {
  color: #45534c;
  background: #f5f8f6;
}

/* 放大 PC 表格及页面按钮文字，提升宽屏阅读体验 */
::v-deep .detail-table .cell {
  font-size: 22px;
  line-height: 32px;
}

/* 详情表格正文数据使用更大的字号，方便 PC 端查看 */
::v-deep .detail-table .el-table__body .cell {
  font-size: 24px !important;
  line-height: 34px;
}

::v-deep .detail-table .el-table__body td {
  padding: 14px 0;
}

::v-deep .detail-table .el-table__empty-text {
  font-size: 22px;
}

::v-deep .page-header .el-button {
  font-size: 22px;
}

/* Element UI 小号状态标签同步放大，避免覆盖页面基础字号 */
::v-deep .profile-name .el-tag {
  height: 38px;
  font-size: 22px;
  line-height: 36px;
}

/* 防止加载遮罩淡出后继续拦截返回等页面操作 */
::v-deep .el-loading-mask.el-loading-fade-leave-active {
  pointer-events: none;
}

@media (max-width: 1100px) {
  .detail-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .ig-employee-detail-page {
    padding: 14px;
  }

  .page-header {
    align-items: flex-start;
    flex-direction: column;
    padding: 18px;

    > .el-button {
      width: 100%;
      margin-top: 16px;
    }
  }

  .page-title {
    font-size: 22px;
  }

  .profile-card {
    align-items: flex-start;
    padding: 20px 16px;
  }

  .profile-avatar {
    flex: 0 0 auto;
    width: 54px;
    height: 54px;
    margin-right: 14px;
    font-size: 22px;
  }

  .profile-name {
    align-items: flex-start;
    flex-direction: column;
    gap: 7px;
    font-size: 22px;
  }

  .profile-meta {
    flex-direction: column;
    gap: 8px;
  }

  .detail-card {
    padding: 18px 14px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .detail-item.wide {
    grid-column: span 1;
  }
}
</style>
